import { v4 as uuid } from 'uuid';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState
} from 'react';
import { AppModule, WindowEvents } from 'utils/constants';
import {
  createQueryParams,
  getReconnectionDelay,
  MAX_RETRIES
} from 'utils/restUtils';
import { Config } from 'utils/envConfig';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import {
  selectAccountInfo,
  selectSelectedEmsMemberAccount,
  selectTenant
} from 'features/account/accountSelectors';
import { selectSseToken } from 'features/dispatcher/selectors/dispatcherSelectors';
import { fetchAndSetSseToken } from 'features/dispatcher/thunks/dispatcherThunks';
import { setSseToken } from 'features/dispatcher/dispatcherSlice';
import { openSnackbar } from 'features/app/appSlice';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

export type EventListenerConfig = {
  type: string;
  handler: (event: MessageEvent<any>) => void;
};

type SseProviderContextType = {
  eventSource: EventSource | null;
};

declare global {
  interface EventSource {
    addEventListeners(listeners: EventListenerConfig[]): void;
    removeEventListeners(listeners: EventListenerConfig[]): void;
  }
}

EventSource.prototype.addEventListeners = function (
  listeners: EventListenerConfig[]
) {
  listeners.forEach(({ type, handler }) => {
    this.addEventListener(type, handler);
  });
};

EventSource.prototype.removeEventListeners = function (
  listeners: EventListenerConfig[]
) {
  listeners.forEach(({ type, handler }) => {
    this.removeEventListener(type, handler);
  });
};

const createEventSource = (tenant: string, sseToken: string): EventSource => {
  const queryParams = createQueryParams({
    token: sseToken,
    tenantId: tenant,
    transactionId: uuid()
  });

  const path = `${Config.apiUrl}/${AppModule.DISPATCHER}/server-sent/events${queryParams}`;
  return new EventSource(path, {
    withCredentials: true
  });
};

const initialState: SseProviderContextType = {
  eventSource: null
};

const getSnackbarOptions = (t: TFunction) => ({
  open: true,
  variant: 'alert',
  message: t('common.httpErrorStatusMessages.error500InternalError'),
  alert: {
    color: 'error'
  },
  close: false
});

const hasBrowserSseSupport = WindowEvents.EVENT_SOURCE in window;
const SseContext = createContext(initialState);
let retryCount = 1;

// ==============================|| CONTEXT ||============================== //

export const SseProvider = ({ children }: PropsWithChildren) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const accountInfo = useAppSelector(selectAccountInfo);
  const emsMemberAccount = useAppSelector(selectSelectedEmsMemberAccount);
  const sseToken = useAppSelector(selectSseToken);
  const tenant = useAppSelector(selectTenant);

  const [eventSource, setEventSource] = useState<EventSource | null>(null);

  useEffect(() => {
    const shouldFetchSseToken =
      !sseToken && tenant && accountInfo?.currentAccount && emsMemberAccount;

    if (!shouldFetchSseToken) return;

    dispatch(fetchAndSetSseToken());
  }, [accountInfo, dispatch, emsMemberAccount, sseToken, tenant]);

  useEffect(() => {
    if (!hasBrowserSseSupport || !sseToken || !tenant || !emsMemberAccount) {
      return;
    }

    setEventSource(createEventSource(tenant, sseToken));
  }, [emsMemberAccount, sseToken, tenant]);

  useEffect(() => {
    if (!eventSource) return;

    eventSource.onerror = () => {
      if (retryCount <= MAX_RETRIES) {
        setTimeout(
          () => dispatch(setSseToken(undefined)),
          getReconnectionDelay(retryCount)
        );
        retryCount++;
      } else {
        dispatch(openSnackbar(getSnackbarOptions(t)));
        eventSource.close();
        retryCount = 1;
      }
    };

    return () => {
      eventSource.close();
    };
  }, [dispatch, eventSource, t]);

  return (
    <SseContext.Provider value={{ eventSource }}>
      {children}
    </SseContext.Provider>
  );
};

export const useSseContext = (): SseProviderContextType => {
  const context = useContext(SseContext);

  if (context == null) {
    throw new Error('No SseContext.Provider found when calling useSse.');
  }

  return context;
};
